// Client-side validation helpers for file selection.
//
// Backend remains the validation authority (§11.9 merge criteria, §8.5).
// These helpers exist so the UI can give early feedback when a user picks
// something obviously wrong (wrong type, too big, too many at once) without
// a round-trip. They MUST NOT be relied on for security.
//
// Pure functions only — no React, no DOM, no async. Live in the entity so
// multiple features (drag-drop, paste, picker) can share them (§5).
//
// Type gate: extension-based (matching the backend ALLOWED_UPLOAD_EXTENSIONS),
// NOT MIME-based. The backend gates on extension; MIME types are advisory only
// and can drift. `validateFileExtension` is the authoritative type check.
// `validateFileType` (MIME) is kept for callers with legacy MIME contracts.

import { ALLOWED_UPLOAD_EXTENSIONS } from './uploadConstraints';

/**
 * Why a discriminated union with stable codes:
 *   The UI branches on `code` to render an icon / aria-label. Mirrors the
 *   wave-3 ChatRuntimeError style: a user-safe message plus a developer
 *   message, never leaked together.
 *
 * `userMessage` names the file and the specific violated constraint so the
 * user knows exactly what to fix. Safe to render verbatim — filename comes
 * from the browser File object and React escapes output by default.
 */
export type ValidationFailureCode = 'type' | 'mime' | 'size' | 'count';

export type ValidationFailure =
  | {
      code: 'type';
      userMessage: string;
      devMessage: string;
      actualExtension: string;
      allowedExtensions: ReadonlyArray<string>;
    }
  | {
      code: 'mime';
      userMessage: string;
      devMessage: string;
      actualMimeType: string;
      allowedMimeTypes: ReadonlyArray<string>;
    }
  | {
      code: 'size';
      userMessage: string;
      devMessage: string;
      actualBytes: number;
      maxBytes: number;
    }
  | {
      code: 'count';
      userMessage: string;
      devMessage: string;
      currentCount: number;
      incomingCount: number;
      maxCount: number;
    };

export type ValidationResult =
  | { ok: true }
  | { ok: false; failure: ValidationFailure };

export type ValidationOptions = {
  /**
   * Extension-based allow-list (with leading dot, lowercase). When provided,
   * `validateFiles` gates on extension — matching the backend authoritative
   * check. Defaults to `ALLOWED_UPLOAD_EXTENSIONS` when omitted so callers
   * that pass no type constraint still get the correct backend-mirrored gate.
   * Pass an empty array `[]` to skip the type check entirely.
   */
  allowedExtensions?: ReadonlyArray<string>;
  /**
   * @deprecated Use `allowedExtensions` for the authoritative gate. MIME types
   * can drift from the backend. This field is kept for callers that have a
   * separate MIME advisory contract; `validateFiles` ignores it.
   */
  allowedMimeTypes?: ReadonlyArray<string>;
  maxBytes?: number;
  maxCount?: number;
  currentCount?: number;
};

export type RejectedFile = {
  file: File;
  reasons: ReadonlyArray<ValidationFailure>;
};

export type ValidationOutcome = {
  accepted: ReadonlyArray<File>;
  rejected: ReadonlyArray<RejectedFile>;
};

function _allowedExtensionsLabel(exts: ReadonlyArray<string>): string {
  return exts.map((e) => e.replace(/^\./, '').toUpperCase()).join(', ');
}

/**
 * Extension-based type gate — authoritative client check matching the backend
 * `ALLOWED_UPLOAD_EXTENSIONS`. Prefer this over `validateFileType` (MIME) for
 * all new callers.
 */
export function validateFileExtension(
  file: File,
  allowedExtensions: ReadonlyArray<string>,
): ValidationResult {
  if (allowedExtensions.length === 0) {
    return { ok: true };
  }
  const dot = file.name.lastIndexOf('.');
  const actualExt = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  if (actualExt !== '' && allowedExtensions.includes(actualExt)) {
    return { ok: true };
  }
  const displayExt = actualExt !== '' ? actualExt : '(no extension)';
  const allowedLabel = _allowedExtensionsLabel(allowedExtensions);
  return {
    ok: false,
    failure: {
      code: 'type',
      userMessage: `"${file.name}" (${displayExt}) isn't a supported type. Allowed: ${allowedLabel}.`,
      devMessage: `Extension "${actualExt || '(none)'}" is not in the allowed list.`,
      actualExtension: actualExt,
      allowedExtensions,
    },
  };
}

/**
 * Match a concrete MIME type against a single allow-list pattern.
 * Patterns may be exact (`image/png`) or wildcard (`image/*`).
 * Both sides are compared case-insensitively per RFC 2045.
 */
function mimeMatches(actual: string, pattern: string): boolean {
  const a = actual.trim().toLowerCase();
  const p = pattern.trim().toLowerCase();
  if (p === '' || a === '') {
    return false;
  }
  if (p === '*' || p === '*/*') {
    return true;
  }
  if (p.endsWith('/*')) {
    const prefix = p.slice(0, p.length - 1);
    return a.startsWith(prefix);
  }
  return a === p;
}

function _mbString(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '');
}

export function validateFileType(
  file: File,
  allowedMimeTypes: ReadonlyArray<string>,
): ValidationResult {
  if (allowedMimeTypes.length === 0) {
    return { ok: true };
  }
  const actual = file.type;
  for (const pattern of allowedMimeTypes) {
    if (mimeMatches(actual, pattern)) {
      return { ok: true };
    }
  }
  const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase() : actual || '(unknown)';
  const allowedList = allowedMimeTypes.join(', ');
  return {
    ok: false,
    failure: {
      code: 'mime',
      userMessage: `"${file.name}" (${ext}) isn't a supported type. Allowed: ${allowedList}.`,
      devMessage: `MIME "${actual || '(empty)'}" did not match any allowed pattern.`,
      actualMimeType: actual,
      allowedMimeTypes,
    },
  };
}

export function validateFileSize(file: File, maxBytes: number): ValidationResult {
  if (maxBytes <= 0) {
    return {
      ok: false,
      failure: {
        code: 'size',
        userMessage: `"${file.name}" cannot be uploaded — the size limit is ${_mbString(maxBytes)} MB. Choose a smaller file.`,
        devMessage: `maxBytes=${maxBytes} rejects all files.`,
        actualBytes: file.size,
        maxBytes,
      },
    };
  }
  if (file.size <= maxBytes) {
    return { ok: true };
  }
  return {
    ok: false,
    failure: {
      code: 'size',
      userMessage: `"${file.name}" is ${_mbString(file.size)} MB, over the ${_mbString(maxBytes)} MB limit. Choose a smaller file.`,
      devMessage: `File size ${file.size} exceeds maxBytes ${maxBytes}.`,
      actualBytes: file.size,
      maxBytes,
    },
  };
}

export function validateFileCount(
  currentCount: number,
  incomingCount: number,
  maxCount: number,
): ValidationResult {
  if (currentCount + incomingCount <= maxCount) {
    return { ok: true };
  }
  const excess = currentCount + incomingCount - maxCount;
  return {
    ok: false,
    failure: {
      code: 'count',
      userMessage: `You can attach up to ${maxCount} files at once. Remove ${excess} to continue.`,
      devMessage: `currentCount(${currentCount}) + incomingCount(${incomingCount}) exceeds maxCount(${maxCount}).`,
      currentCount,
      incomingCount,
      maxCount,
    },
  };
}

/**
 * Orchestrator. Per-file checks (type, size) collect ALL failures for a
 * file; the file appears once in `rejected` with every reason. The batch
 * count check gates on accepted-so-far: files that already fail type/size
 * are excluded from the count, so the count failure only lands on files that
 * would otherwise be accepted but exceed the cap.
 *
 * Type gate: extension-based by default (matching the backend authoritative
 * gate). `allowedExtensions` defaults to `ALLOWED_UPLOAD_EXTENSIONS` when
 * omitted. Pass `allowedExtensions: []` to skip the type check.
 */
export function validateFiles(
  files: ReadonlyArray<File>,
  options: ValidationOptions,
): ValidationOutcome {
  if (files.length === 0) {
    return { accepted: [], rejected: [] };
  }

  // Extension-based gate — defaults to backend-mirrored list so callers that
  // pass no type constraint still get the correct check.
  const allowedExtensions: ReadonlyArray<string> =
    options.allowedExtensions !== undefined
      ? options.allowedExtensions
      : ALLOWED_UPLOAD_EXTENSIONS;

  const maxBytes = options.maxBytes ?? Number.POSITIVE_INFINITY;
  const maxCount = options.maxCount ?? Number.POSITIVE_INFINITY;
  const currentCount = options.currentCount ?? 0;

  // First pass: per-file type + size checks (independent of count).
  const perFileResults: { file: File; reasons: ValidationFailure[] }[] = [];
  for (const file of files) {
    const reasons: ValidationFailure[] = [];

    const typeResult = validateFileExtension(file, allowedExtensions);
    if (!typeResult.ok) {
      reasons.push(typeResult.failure);
    }

    const sizeResult = validateFileSize(file, maxBytes);
    if (!sizeResult.ok) {
      reasons.push(sizeResult.failure);
    }

    perFileResults.push({ file, reasons });
  }

  // Second pass: count gate — only apply to files that passed type+size.
  // This way the count message accurately reflects how many valid files are
  // being added, and rejected-by-type/size files don't inflate the count.
  const typeAndSizeOk = perFileResults.filter((r) => r.reasons.length === 0);
  const countResult = validateFileCount(currentCount, typeAndSizeOk.length, maxCount);

  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];

  for (const { file, reasons } of perFileResults) {
    // For files already rejected by type/size, keep their existing reasons.
    if (reasons.length > 0) {
      rejected.push({ file, reasons });
      continue;
    }
    // For files that passed type+size, apply the count gate.
    if (!countResult.ok) {
      rejected.push({ file, reasons: [countResult.failure] });
    } else {
      accepted.push(file);
    }
  }

  return { accepted, rejected };
}

export function describeFailure(failure: ValidationFailure): string {
  switch (failure.code) {
    case 'type':
      return `File type "${failure.actualExtension || '(none)'}" is not allowed.`;
    case 'mime':
      return 'File type is not allowed.';
    case 'size':
      return `File exceeds the maximum size of ${failure.maxBytes} bytes.`;
    case 'count':
      return `Cannot add ${failure.incomingCount} more file(s); limit is ${failure.maxCount}.`;
  }
}
